import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Save, 
  CheckCircle, 
  AlertTriangle, 
  Download, 
  TestTube,
  User,
  Target,
  BarChart3,
  Eye,
  MousePointer,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface AdvertiserProfile {
  id: string;
  name: string;
  business_description: string;
  contact_email: string;
  contact_phone?: string;
  whatsapp?: string;
  website?: string;
  address?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  snapchat?: string;
  tracking_tag_type: 'meta' | 'google' | 'other' | 'none';
  tag_id: string;
  tag_status: 'active' | 'missing' | 'inactive';
  last_activity: string | null;
  use_fallback_tag: boolean;
  admin_notes: string;
  total_ads: number;
  total_impressions: number;
  total_clicks: number;
  ctr: number;
  total_spend: number;
  created_at: string;
  updated_at: string;
}

const AdminAdvertiserProfile = () => {
  const { advertiserId } = useParams<{ advertiserId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const { toast } = useToast();

  const [advertiser, setAdvertiser] = useState<AdvertiserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadAdvertiserData();
  }, [advertiserId]);

  const loadAdvertiserData = async () => {
    try {
      setLoading(true);
      
      // Handle creation of new advertiser
      if (advertiserId === 'new') {
        setAdvertiser({
          id: 'new',
          name: '',
          business_description: '',
          contact_email: '',
          contact_phone: '',
          whatsapp: '',
          website: '',
          address: '',
          facebook: '',
          instagram: '',
          tiktok: '',
          snapchat: '',
          tracking_tag_type: 'none',
          tag_id: '',
          tag_status: 'missing',
          last_activity: null,
          use_fallback_tag: true,
          admin_notes: '',
          total_ads: 0,
          total_impressions: 0,
          total_clicks: 0,
          ctr: 0,
          total_spend: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        return;
      }
      
      // Load existing advertiser from Supabase
      const { data: advertiserData, error } = await supabase
        .from('advertisers')
        .select('*')
        .eq('id', advertiserId)
        .single();
      
      if (error) throw error;
      
      if (!advertiserData) {
        toast({
          title: isSpanish ? "Error" : "Error",
          description: isSpanish ? "Anunciante no encontrado" : "Advertiser not found",
          variant: "destructive"
        });
        navigate('/admin/advertisers');
        return;
      }

      // Fetch ads for this advertiser
      const { data: adsData } = await supabase
        .from('ads')
        .select('*')
        .eq('company_name', advertiserData.company_name);

      const totalImpressions = (adsData || []).reduce((sum, ad) => sum + (ad.impressions || 0), 0);
      const totalClicks = (adsData || []).reduce((sum, ad) => sum + (ad.clicks || 0), 0);
      const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
      const totalSpend = (adsData || []).reduce((sum, ad) => sum + (Number(ad.amount_spent) || 0), 0);

      // Map database fields to component interface
      setAdvertiser({
        id: advertiserData.id,
        name: advertiserData.company_name,
        business_description: advertiserData.business_description || '',
        contact_email: advertiserData.contact_email || '',
        contact_phone: advertiserData.contact_phone || '',
        whatsapp: advertiserData.whatsapp || '',
        website: advertiserData.company_website || advertiserData.website || '',
        address: advertiserData.company_address || advertiserData.address || '',
        facebook: advertiserData.facebook || '',
        instagram: advertiserData.instagram || '',
        tiktok: advertiserData.tiktok || '',
        snapchat: advertiserData.snapchat || '',
        tracking_tag_type: advertiserData.meta_pixel_id ? 'meta' : advertiserData.google_tag_id ? 'google' : 'none',
        tag_id: advertiserData.meta_pixel_id || advertiserData.google_tag_id || '',
        tag_status: (advertiserData.tracking_status || 'missing') as 'active' | 'inactive' | 'missing',
        last_activity: advertiserData.last_activity,
        use_fallback_tag: advertiserData.default_tracking_pixel_enabled !== false,
        admin_notes: advertiserData.internal_notes || advertiserData.notes || '',
        total_ads: (adsData || []).length,
        total_impressions: totalImpressions,
        total_clicks: totalClicks,
        ctr,
        total_spend: totalSpend,
        created_at: advertiserData.created_at,
        updated_at: advertiserData.updated_at
      });
    } catch (error) {
      console.error('Error loading advertiser:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al cargar datos del anunciante" : "Failed to load advertiser data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!advertiser) return;
    
    setSaving(true);
    try {
      const dataToSave = {
        company_name: advertiser.name,
        business_description: advertiser.business_description,
        contact_email: advertiser.contact_email,
        contact_phone: advertiser.contact_phone,
        whatsapp: advertiser.whatsapp,
        company_website: advertiser.website,
        company_address: advertiser.address,
        facebook: advertiser.facebook,
        instagram: advertiser.instagram,
        tiktok: advertiser.tiktok,
        snapchat: advertiser.snapchat,
        meta_pixel_id: advertiser.tracking_tag_type === 'meta' ? advertiser.tag_id : null,
        google_tag_id: advertiser.tracking_tag_type === 'google' ? advertiser.tag_id : null,
        utm_campaign_name: advertiser.name.toLowerCase().replace(/\s+/g, '_'),
        default_tracking_pixel_enabled: advertiser.use_fallback_tag,
        internal_notes: advertiser.admin_notes,
        is_active: true,
        updated_at: new Date().toISOString()
      };

      if (advertiser.id === 'new') {
        // Create new advertiser
        const { data, error } = await supabase
          .from('advertisers')
          .insert(dataToSave)
          .select()
          .single();
        
        if (error) throw error;
        
        toast({
          title: isSpanish ? "Creado" : "Created",
          description: isSpanish ? "Nuevo anunciante creado exitosamente" : "New advertiser created successfully"
        });
        
        // Navigate to the new advertiser's profile
        navigate(`/admin/advertiser/${data.id}`);
      } else {
        // Update existing advertiser
        const { error } = await supabase
          .from('advertisers')
          .update(dataToSave)
          .eq('id', advertiser.id);
        
        if (error) throw error;
        
        setAdvertiser({
          ...advertiser,
          updated_at: new Date().toISOString()
        });
        
        toast({
          title: isSpanish ? "Guardado" : "Saved",
          description: isSpanish ? "Perfil del anunciante actualizado" : "Advertiser profile updated"
        });
        
        // Reload to get updated metrics
        loadAdvertiserData();
      }
    } catch (error) {
      console.error('Error saving advertiser:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al guardar cambios" : "Failed to save changes",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestTracking = async () => {
    if (!advertiser || !advertiser.tag_id) {
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "No hay ID de tag para probar" : "No tag ID to test",
        variant: "destructive"
      });
      return;
    }

    setTesting(true);
    try {
      // Simulate tracking validation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success/failure
      const isValid = Math.random() > 0.3;
      
      if (isValid) {
        setAdvertiser(prev => prev ? {
          ...prev,
          tag_status: 'active',
          last_activity: new Date().toISOString()
        } : null);
        
        toast({
          title: isSpanish ? "Prueba Exitosa" : "Test Successful",
          description: isSpanish ? "El tag de seguimiento está funcionando correctamente" : "Tracking tag is working correctly"
        });
      } else {
        toast({
          title: isSpanish ? "Prueba Fallida" : "Test Failed",
          description: isSpanish ? "El tag de seguimiento no responde" : "Tracking tag is not responding",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error testing tracking:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al probar el seguimiento" : "Failed to test tracking",
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  const handleExportData = () => {
    if (!advertiser) return;

    const csvData = [
      ['Field', 'Value'],
      ['Name', advertiser.name],
      ['Business Description', advertiser.business_description],
      ['Contact Email', advertiser.contact_email],
      ['Contact Phone', advertiser.contact_phone || ''],
      ['WhatsApp', advertiser.whatsapp || ''],
      ['Website', advertiser.website || ''],
      ['Facebook', advertiser.facebook || ''],
      ['Instagram', advertiser.instagram || ''],
      ['TikTok', advertiser.tiktok || ''],
      ['Snapchat', advertiser.snapchat || ''],
      ['Address', advertiser.address || ''],
      ['Tracking Tag Type', advertiser.tracking_tag_type],
      ['Tag ID', advertiser.tag_id],
      ['Tag Status', advertiser.tag_status],
      ['Last Activity', advertiser.last_activity ? format(new Date(advertiser.last_activity), 'yyyy-MM-dd HH:mm') : 'Never'],
      ['Use Fallback Tag', advertiser.use_fallback_tag ? 'Yes' : 'No'],
      ['Admin Notes', advertiser.admin_notes],
      ['Total Ads', advertiser.total_ads.toString()],
      ['Total Impressions', advertiser.total_impressions.toString()],
      ['Total Clicks', advertiser.total_clicks.toString()],
      ['CTR (%)', advertiser.ctr.toFixed(2)],
      ['Total Spend ($)', advertiser.total_spend.toFixed(2)],
      ['Created At', format(new Date(advertiser.created_at), 'yyyy-MM-dd HH:mm')],
      ['Updated At', format(new Date(advertiser.updated_at), 'yyyy-MM-dd HH:mm')]
    ];

    const csvContent = csvData
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `advertiser_${advertiser.id}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();

    toast({
      title: isSpanish ? "Exportado" : "Exported",
      description: isSpanish ? "Datos del anunciante exportados" : "Advertiser data exported"
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />{isSpanish ? 'Activo' : 'Active'}</Badge>;
      case 'missing':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />{isSpanish ? 'Faltante' : 'Missing'}</Badge>;
      case 'inactive':
        return <Badge variant="secondary"><AlertTriangle className="w-3 h-3 mr-1" />{isSpanish ? 'Inactivo' : 'Inactive'}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!advertiser) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          {isSpanish ? "Anunciante no encontrado" : "Advertiser not found"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/advertisers')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {isSpanish ? 'Volver a Anunciantes' : 'Back to Advertisers'}
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {advertiser.id === 'new' 
                ? (isSpanish ? 'Crear Nuevo Anunciante' : 'Create New Advertiser')
                : advertiser.name
              }
            </h1>
            <p className="text-muted-foreground">
              {isSpanish ? 'Perfil del Anunciante' : 'Advertiser Profile'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportData}>
            <Download className="h-4 w-4 mr-2" />
            {isSpanish ? 'Exportar' : 'Export'}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? (isSpanish ? 'Guardando...' : 'Saving...') : 
             (advertiser.id === 'new' ? (isSpanish ? 'Crear' : 'Create') : (isSpanish ? 'Guardar' : 'Save'))}
          </Button>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className="flex gap-4 mb-6">
        <Button 
          variant="default" 
          onClick={() => navigate('/admin/ads/new', { state: { advertiserId: advertiser.id, companyName: advertiser.name } })}
          className="flex items-center gap-2"
        >
          <Target className="h-4 w-4" />
          {isSpanish ? 'Crear Anuncio' : 'Create New Ad'}
        </Button>
        
        <Button 
          variant="outline" 
          onClick={handleExportData}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          {isSpanish ? 'Exportar Rendimiento' : 'Export Performance'}
        </Button>
        
        <Button 
          variant="outline" 
          onClick={() => navigate(`/admin/advertisers/${advertiser.id}/billing`)}
          className="flex items-center gap-2"
        >
          <DollarSign className="h-4 w-4" />
          {isSpanish ? 'Gestionar Facturación' : 'Manage Billing'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Logo and Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {isSpanish ? 'Información del Anunciante' : 'Advertiser Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{isSpanish ? 'Nombre' : 'Name'}</Label>
              <Input
                id="name"
                value={advertiser.name}
                onChange={(e) => setAdvertiser({...advertiser, name: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="business_description">{isSpanish ? 'Descripción del Negocio' : 'Business Description'}</Label>
              <Textarea
                id="business_description"
                value={advertiser.business_description}
                onChange={(e) => setAdvertiser({...advertiser, business_description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="contact_email">{isSpanish ? 'Email de Contacto' : 'Contact Email'}</Label>
              <Input
                id="contact_email"
                type="email"
                value={advertiser.contact_email}
                onChange={(e) => setAdvertiser({...advertiser, contact_email: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="contact_phone">{isSpanish ? 'Teléfono' : 'Phone'}</Label>
              <Input
                id="contact_phone"
                value={advertiser.contact_phone || ''}
                onChange={(e) => setAdvertiser({...advertiser, contact_phone: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="whatsapp">WhatsApp</Label>
              <Input
                id="whatsapp"
                value={advertiser.whatsapp || ''}
                onChange={(e) => setAdvertiser({...advertiser, whatsapp: e.target.value})}
                placeholder="+52 664 123 4567"
              />
            </div>

            <div>
              <Label htmlFor="website">{isSpanish ? 'Sitio Web' : 'Website'}</Label>
              <Input
                id="website"
                value={advertiser.website || ''}
                onChange={(e) => setAdvertiser({...advertiser, website: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="facebook">Facebook</Label>
              <Input
                id="facebook"
                value={advertiser.facebook || ''}
                onChange={(e) => setAdvertiser({...advertiser, facebook: e.target.value})}
                placeholder="https://facebook.com/yourpage"
              />
            </div>

            <div>
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                id="instagram"
                value={advertiser.instagram || ''}
                onChange={(e) => setAdvertiser({...advertiser, instagram: e.target.value})}
                placeholder="https://instagram.com/youraccount"
              />
            </div>

            <div>
              <Label htmlFor="tiktok">TikTok</Label>
              <Input
                id="tiktok"
                value={advertiser.tiktok || ''}
                onChange={(e) => setAdvertiser({...advertiser, tiktok: e.target.value})}
                placeholder="https://tiktok.com/@youraccount"
              />
            </div>

            <div>
              <Label htmlFor="snapchat">Snapchat</Label>
              <Input
                id="snapchat"
                value={advertiser.snapchat || ''}
                onChange={(e) => setAdvertiser({...advertiser, snapchat: e.target.value})}
                placeholder="https://snapchat.com/add/yourusername"
              />
            </div>

            <div>
              <Label htmlFor="address">{isSpanish ? 'Dirección' : 'Address'}</Label>
              <Textarea
                id="address"
                value={advertiser.address || ''}
                onChange={(e) => setAdvertiser({...advertiser, address: e.target.value})}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Tracking Configuration Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              {isSpanish ? 'Configuración de Seguimiento' : 'Tracking Configuration'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="tracking_tag_type">{isSpanish ? 'Tipo de Tag de Seguimiento' : 'Tracking Tag Type'}</Label>
              <Select
                value={advertiser.tracking_tag_type}
                onValueChange={(value: 'meta' | 'google' | 'other' | 'none') => 
                  setAdvertiser({...advertiser, tracking_tag_type: value})
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="meta">Meta Pixel</SelectItem>
                  <SelectItem value="google">Google Tag</SelectItem>
                  <SelectItem value="other">{isSpanish ? 'Otro' : 'Other'}</SelectItem>
                  <SelectItem value="none">{isSpanish ? 'Ninguno' : 'None'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tag_id">{isSpanish ? 'ID del Tag' : 'Tag ID'}</Label>
              <Input
                id="tag_id"
                value={advertiser.tag_id}
                onChange={(e) => setAdvertiser({...advertiser, tag_id: e.target.value})}
                placeholder={advertiser.tracking_tag_type === 'meta' ? '123456789012345' : 
                           advertiser.tracking_tag_type === 'google' ? 'GTM-XXXXXXX' : 
                           isSpanish ? 'ID único del tag' : 'Unique tag ID'}
              />
            </div>

            <div>
              <Label htmlFor="tag_status">{isSpanish ? 'Estado del Tag' : 'Tag Status'}</Label>
              <div className="flex items-center gap-2">
                {getStatusBadge(advertiser.tag_status)}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleTestTracking}
                  disabled={testing || !advertiser.tag_id}
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  {testing ? (isSpanish ? 'Probando...' : 'Testing...') : (isSpanish ? 'Probar' : 'Test')}
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="last_activity">{isSpanish ? 'Última Actividad' : 'Last Activity'}</Label>
              <Input
                id="last_activity"
                value={advertiser.last_activity ? 
                  format(new Date(advertiser.last_activity), 'MMM dd, yyyy HH:mm') : 
                  (isSpanish ? 'Nunca' : 'Never')
                }
                readOnly
                className="bg-muted"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="use_fallback_tag"
                checked={advertiser.use_fallback_tag}
                onCheckedChange={(checked) => setAdvertiser({...advertiser, use_fallback_tag: !!checked})}
              />
              <Label htmlFor="use_fallback_tag">
                {isSpanish ? 'Usar tag de respaldo si no se proporciona uno' : 'Use fallback tag if none provided'}
              </Label>
            </div>

            <div>
              <Label htmlFor="admin_notes">{isSpanish ? 'Notas de Admin' : 'Admin Notes'}</Label>
              <Textarea
                id="admin_notes"
                value={advertiser.admin_notes}
                onChange={(e) => setAdvertiser({...advertiser, admin_notes: e.target.value})}
                rows={3}
                placeholder={isSpanish ? 'Comentarios internos...' : 'Internal comments...'}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ad Campaign History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {isSpanish ? 'Historial de Campañas Publicitarias' : 'Ad Campaign History'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {advertiser.id !== 'new' ? (
              <div className="space-y-3">
                {/* Sample ad campaigns for this advertiser */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{isSpanish ? 'Anuncio de Menú Especial' : 'Special Menu Ad'}</h4>
                    <Badge variant="default">{isSpanish ? 'Activo' : 'Active'}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{isSpanish ? 'Impresiones' : 'Impressions'}</p>
                      <p className="font-medium">3,420</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{isSpanish ? 'Clics' : 'Clicks'}</p>
                      <p className="font-medium">198</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">CTR</p>
                      <p className="font-medium">5.79%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{isSpanish ? 'Gasto' : 'Spend'}</p>
                      <p className="font-medium">$420.50</p>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{isSpanish ? 'Promoción Fin de Semana' : 'Weekend Promotion'}</h4>
                    <Badge variant="secondary">{isSpanish ? 'Completado' : 'Completed'}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{isSpanish ? 'Impresiones' : 'Impressions'}</p>
                      <p className="font-medium">2,180</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{isSpanish ? 'Clics' : 'Clicks'}</p>
                      <p className="font-medium">127</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">CTR</p>
                      <p className="font-medium">5.83%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{isSpanish ? 'Gasto' : 'Spend'}</p>
                      <p className="font-medium">$280.00</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button variant="outline" size="sm">
                    {isSpanish ? 'Ver Todas las Campañas' : 'View All Campaigns'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>{isSpanish ? 'No hay campañas publicitarias aún.' : 'No ad campaigns yet.'}</p>
                <p className="text-sm">{isSpanish ? 'Crea tu primer anuncio para ver las métricas aquí.' : 'Create your first ad to see metrics here.'}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ad Activity Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {isSpanish ? 'Resumen de Actividad Publicitaria' : 'Ad Activity Overview'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingUp className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-2xl font-bold">{advertiser.total_ads}</p>
              <p className="text-sm text-muted-foreground">
                {isSpanish ? 'Anuncios Totales' : 'Total Ads'}
              </p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Eye className="h-4 w-4 text-purple-500" />
              </div>
              <p className="text-2xl font-bold">{advertiser.total_impressions.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {isSpanish ? 'Impresiones' : 'Impressions'}
              </p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <MousePointer className="h-4 w-4 text-green-500" />
              </div>
              <p className="text-2xl font-bold">{advertiser.total_clicks.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {isSpanish ? 'Clics' : 'Clicks'}
              </p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Target className="h-4 w-4 text-orange-500" />
              </div>
              <p className="text-2xl font-bold">{advertiser.ctr.toFixed(2)}%</p>
              <p className="text-sm text-muted-foreground">CTR</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-2xl font-bold">${advertiser.total_spend.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {isSpanish ? 'Gasto Total' : 'Total Spend'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>{isSpanish ? 'Creado:' : 'Created:'}</strong> {format(new Date(advertiser.created_at), 'MMM dd, yyyy HH:mm')}</p>
            <p><strong>{isSpanish ? 'Actualizado:' : 'Updated:'}</strong> {format(new Date(advertiser.updated_at), 'MMM dd, yyyy HH:mm')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAdvertiserProfile;